export default function BenefitsSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            Benefits of Selling to Avoid Foreclosure
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Choosing to sell your El Paso home before foreclosure gives you control over your situation and provides numerous advantages over letting the foreclosure process complete.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/a6818ec807254d8ee97141ce65a98941.jpg" 
              alt="Benefits of avoiding foreclosure by selling your home" 
              className="w-full h-auto rounded-2xl shadow-xl"
            />
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-teal-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-shield-check-fill text-teal-600 mr-3 text-2xl"></i>
                Protect Your Credit Score
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">
                A foreclosure can drop your credit score by 200-400 points and stay on your report for 7 years. Selling before foreclosure minimizes credit damage, typically affecting your score by only 50-100 points. You'll be able to buy another home much sooner and qualify for better interest rates.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-teal-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-money-dollar-circle-fill text-teal-600 mr-3 text-2xl"></i>
                Walk Away with Cash
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">
                When you avoid foreclosure by selling to us, you receive cash at closing. This money can help you relocate, pay off other debts, or start fresh. In foreclosure, you get nothing - the bank takes everything, and you may even owe a deficiency judgment.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-teal-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-time-fill text-teal-600 mr-3 text-2xl"></i>
                Control Your Timeline
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">
                Foreclosure happens on the bank's schedule, often with little notice. When you sell to avoid foreclosure, you choose your closing date and moving timeline. We can close in 7 days or give you more time if needed - you're in control.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-teal-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-emotion-happy-fill text-teal-600 mr-3 text-2xl"></i>
                Maintain Your Dignity
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">
                Foreclosure is public record and can be emotionally devastating. Selling your home allows you to avoid foreclosure quietly and maintain your privacy. You can tell friends and family you sold your house, not that it was foreclosed on.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12 text-white mb-16">
          <h3 className="text-3xl font-black mb-8 text-center">Compare Your Options</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                  <i className="ri-close-line text-3xl text-white"></i>
                </div>
              </div>
              <h4 className="text-2xl font-bold text-center mb-6">Letting Foreclosure Happen</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Credit score drops 200-400 points</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Foreclosure stays on record for 7 years</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">You receive $0 at sale</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Possible deficiency judgment</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Forced eviction by sheriff</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Public record and embarrassment</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Can't buy home for 3-7 years</span>
                </li>
              </ul>
            </div>

            <div className="bg-teal-600/20 backdrop-blur-sm rounded-xl p-8 border-2 border-teal-400">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-3xl text-white"></i>
                </div>
              </div>
              <h4 className="text-2xl font-bold text-center mb-6">Selling to Avoid Foreclosure</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-circle-fill text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Minimal credit impact (50-100 points)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-circle-fill text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Recover credit score much faster</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-circle-fill text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Walk away with cash in hand</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-circle-fill text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">No deficiency judgment risk</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-circle-fill text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Choose your moving date</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-circle-fill text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Private transaction, maintain dignity</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-circle-fill text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base">Buy again in 1-2 years typically</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="relative rounded-2xl overflow-hidden">
          <img 
            src="https://readdy.ai/api/search-image?query=family-unpacking-boxes-in-new-bright-apartment-smiling-and-happy-fresh-start-concept-natural-lighting-showing-positive-future-after-avoiding-foreclosure-realistic-photography-warm-welcoming-home-atmosphere-hope-and-new-beginnings&width=1400&height=600&seq=avoid-foreclosure-new-start-010&orientation=landscape" 
            alt="Fresh start after avoiding foreclosure in El Paso" 
            className="w-full h-auto"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 to-teal-900/60 flex items-center">
            <div className="max-w-2xl mx-auto px-8 text-center">
              <h3 className="text-3xl md:text-4xl font-black text-white mb-6">
                Choose the Better Path
              </h3>
              <p className="text-lg text-gray-200 mb-8 leading-relaxed">
                Don't let foreclosure destroy your financial future. Sell your El Paso home to us and avoid foreclosure with cash in your pocket and your dignity intact.
              </p>
              <a 
                href="tel:+1-915-206-5814" 
                className="inline-block bg-white hover:bg-gray-100 text-gray-900 px-10 py-4 rounded-lg text-lg font-bold transition-all shadow-2xl cursor-pointer whitespace-nowrap"
              >
                Get Your Cash Offer Today
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}